import { useState } from "react";
import { CalendarDays, MapPin, MessageSquare, Star, ChevronDown, Plane, Camera, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { money } from "@/lib/format";
import { InspectionDialog } from "@/components/InspectionDialog";

const today = new Date().toISOString().slice(0, 10);
const STATUS = {
  confirmed: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  completed: "bg-blue-100 text-blue-800",
  cancelled: "bg-slate-100 text-slate-600",
};
function bucket(b) {
  if (b.status === "cancelled") return "cancelled";
  if ((b.return_date || "") < today) return "completed";
  if ((b.pickup_date || "") <= today) return "active";
  return "upcoming";
}

const Row = ({ k, v, bold }) => (
  <div className="flex justify-between">
    <span className="text-slate-500">{k}</span>
    <span className={bold ? "font-bold text-slate-900" : "text-slate-700"}>{v}</span>
  </div>
);

export function CustomerTrips({ bookings, pendingIds, onReview, onGoMessages, onRefresh }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("upcoming");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [photoDlg, setPhotoDlg] = useState(null);

  if (bookings === null) return <p className="text-slate-400">{t("common.loading")}</p>;
  const counts = ["upcoming", "active", "completed", "cancelled"].reduce((a, k) => {
    a[k] = bookings.filter((b) => bucket(b) === k).length;
    return a;
  }, {});
  const filtered = bookings.filter((b) => bucket(b) === tab && (!q || (b.car_name || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div>
      <div className="flex gap-2 border-b border-slate-200 mb-6 overflow-x-auto">
        {["upcoming", "active", "completed", "cancelled"].map((k) => (
          <button key={k} data-testid={`trips-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-3 font-heading font-medium text-sm capitalize border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === k ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            {t(`trips.${k}`)} ({counts[k]})
          </button>
        ))}
      </div>

      <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="trips-search" className="belo-input w-full max-w-xs mb-5" placeholder={t("trips.searchPlaceholder")} />

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400" data-testid="trips-empty">{t("trips.empty", { tab: t(`trips.${tab}`) })}</div>
      ) : (
        <div className="space-y-4" data-testid="customer-trips-list">
          {filtered.map((b) => {
            const canReview = pendingIds.has(b.id);
            const isOpen = open === b.id;
            return (
              <div key={b.id} data-testid={`trip-list-card-${b.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
                <div className="flex flex-col md:flex-row md:items-center gap-4 md:justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-heading text-lg font-semibold text-slate-900">{b.car_name}</h3>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS[b.status] || "bg-slate-100 text-slate-600"}`}>{b.status}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1.5"><CalendarDays size={14} />{b.pickup_date} → {b.return_date} ({t("common.daysShort", { count: b.days })})</span>
                      {b.pickup_location && <span className="flex items-center gap-1.5"><MapPin size={14} />{b.pickup_location.split(",")[1]?.trim() || b.pickup_location}</span>}
                      {b.airport_dropoff && <span className="flex items-center gap-1.5 text-blue-600"><Plane size={14} />Logan</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-xl font-bold text-slate-900">{money(b.payment_plan === "split" ? (b.total_due || b.amount_due) : b.amount_due)}</p>
                    <p className="text-xs mt-0.5">{b.payment_status === "paid" ? <span className="text-emerald-600 font-medium">{t("trips.paid")}</span> : <span className="text-amber-600 font-medium">{t("trips.unpaid")}</span>}</p>
                    {b.payment_plan === "split" && <p data-testid={`trip-split-tag-${b.id}`} className="text-[11px] mt-0.5 text-blue-600 font-semibold">Weekly plan</p>}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 pt-4 border-t border-slate-100">
                  <button onClick={() => setOpen(isOpen ? null : b.id)} data-testid={`trip-expand-${b.id}`} className="text-sm font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
                    {t("trips.details")} <ChevronDown size={15} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  <button onClick={() => onGoMessages(b.id)} data-testid={`trip-message-${b.id}`} className="text-sm font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1.5"><MessageSquare size={15} />{t("trips.messageOwner")}</button>
                  {canReview && (
                    <button onClick={() => onReview({ booking_id: b.id, car_name: b.car_name, counterparty: b.host_name || "Belo Rentals" })} data-testid={`trip-review-${b.id}`} className="text-sm font-semibold text-amber-600 hover:text-amber-700 inline-flex items-center gap-1.5"><Star size={15} />{t("trips.leaveReview")}</button>
                  )}
                  {b.payment_status === "paid" && (tab === "active" || tab === "completed") && !b.photos_purged && (
                    <>
                      {!b.trip_started_at ? (
                        <button onClick={() => setPhotoDlg({ id: b.id, stage: "customer_start", min: 5 })} data-testid={`trip-start-photos-${b.id}`} className="text-sm font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1.5"><Camera size={15} />{t("tripPhotos.addStart")}</button>
                      ) : (
                        <span className="text-sm text-emerald-600 inline-flex items-center gap-1.5"><CheckCircle2 size={15} />{t("tripPhotos.started")}</span>
                      )}
                      {b.trip_started_at && (
                        (b.trip_photos?.customer_return?.length) ? (
                          <span className="text-sm text-emerald-600 inline-flex items-center gap-1.5"><CheckCircle2 size={15} />{t("tripPhotos.returnDone")}</span>
                        ) : (
                          <button onClick={() => setPhotoDlg({ id: b.id, stage: "customer_return", min: 4 })} data-testid={`trip-return-photos-${b.id}`} className="text-sm font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1.5"><Camera size={15} />{t("tripPhotos.addReturn")}</button>
                        )
                      )}
                    </>
                  )}
                  {b.photos_purged && <span className="text-xs text-slate-400 inline-flex items-center gap-1.5">{t("tripPhotos.purged")}</span>}
                </div>

                {isOpen && (
                  <>
                  <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm" data-testid={`trip-details-${b.id}`}>
                    <Row k={t("trips.rental")} v={money(b.rental)} />
                    {b.service_fee > 0 && <Row k={t("trips.serviceFee", { pct: Math.round((b.service_fee_rate || 0.1) * 100) })} v={money(b.service_fee)} />}
                    <Row k={t("trips.tax")} v={money(b.tax)} />
                    {b.unlimited_miles_fee > 0 && <Row k={t("trips.unlimitedMiles")} v={money(b.unlimited_miles_fee)} />}
                    {b.young_driver_fee > 0 && <Row k={t("trips.youngDriverFee")} v={money(b.young_driver_fee)} />}
                    {b.airport_fee > 0 && <Row k={t("trips.airportDropoff")} v={money(b.airport_fee)} />}
                    {b.discount_amount > 0 && <Row k={t("trips.discount", { code: b.discount_code })} v={`−${money(b.discount_amount)}`} />}
                    <Row k={t("trips.refundableDeposit")} v={money(b.deposit)} />
                    <Row k={b.payment_plan === "split" ? "Trip total" : t("trips.totalPaid")} v={money(b.payment_plan === "split" ? (b.total_due || b.amount_due) : b.amount_due)} bold />
                    <Row k={t("trips.mileage")} v={b.unlimited_miles ? t("trips.unlimited") : `${(b.included_miles || 0).toLocaleString()} mi`} />
                    <Row k={t("trips.agreement")} v={b.agreement_signed ? t("trips.signedBy", { name: b.signature_name }) : "—"} />
                  </div>
                  {b.payment_plan === "split" && b.installments?.length > 0 && (
                    <div data-testid={`payment-schedule-${b.id}`} className="mt-4 pt-4 border-t border-slate-100">
                      <p className="label-caps text-slate-400 mb-3 flex items-center gap-1.5"><CalendarDays size={13} /> Weekly payment schedule</p>
                      <div className="space-y-2">
                        {b.installments.map((inst) => (
                          <div key={inst.index} data-testid={`installment-${b.id}-${inst.index}`} className="flex items-center justify-between text-sm">
                            <span className="text-slate-600">Week {inst.index} · <span className="text-slate-400">{inst.due_date}{inst.index === 1 ? " (today)" : ""}</span></span>
                            <span className="flex items-center gap-2.5">
                              <span className="font-semibold text-slate-900">{money(inst.amount)}</span>
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${inst.status === "paid" ? "bg-emerald-100 text-emerald-700" : inst.status === "failed" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                                {inst.status === "paid" ? "Paid" : inst.status === "failed" ? "Failed" : "Scheduled"}
                              </span>
                            </span>
                          </div>
                        ))}
                        <div className="flex justify-between text-sm pt-2.5 mt-1 border-t border-slate-100">
                          <span className="text-slate-500">Total over {b.days} days</span>
                          <span className="font-bold text-slate-900">{money(b.total_due || b.amount_due)}</span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 mt-3">Your saved card is charged automatically each week. The refundable ${b.deposit} deposit is included in week 1 and settled after your trip.</p>
                    </div>
                  )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      {photoDlg && (
        <InspectionDialog
          open={!!photoDlg}
          onClose={() => setPhotoDlg(null)}
          endpoint={`/my/bookings/${photoDlg.id}/photos`}
          stage={photoDlg.stage}
          onDone={onRefresh}
        />
      )}
    </div>
  );
}
